import { announce } from '@src/utils/a11yAnnouncer';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ドキュメント言語管理フック
 *
 * HTMLドキュメントのlang属性を動的に更新し、
 * スクリーンリーダーが正しい言語で読み上げられるようにします。
 *
 * @returns {void}
 */
export const useDocumentLanguage = (): void => {
  const { i18n } = useTranslation();

  useEffect(() => {
    // HTML要素のlang属性を更新
    document.documentElement.lang = i18n.language;

    // dir属性も設定（RTL言語サポート）
    const isRTL = ['ar', 'he', 'fa', 'ur'].includes(i18n.language);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';

    // 言語変更をスクリーンリーダーに通知（パフォーマンス最適化：共通ユーティリティを使用）
    const languageNames: Record<string, string> = {
      ja: '日本語',
      en: 'English',
      ko: '한국어',
      'zh-CN': '简体中文',
      'zh-TW': '繁體中文',
    };

    announce(
      `言語を${languageNames[i18n.language] || i18n.language}に切り替えました`,
      'polite',
      3000
    );
  }, [i18n.language]);
};

/**
 * ページタイトル管理フック
 *
 * ページタイトルを動的に更新し、スクリーンリーダーユーザーに
 * 現在のページ位置を明確に伝えます。
 *
 * @param {string} pageTitle - ページタイトル
 * @param {string} [suffix] - サフィックス（サイト名など）
 * @returns {void}
 */
const _usePageTitle = (pageTitle: string, suffix = 'Resume Commit'): void => {
  const { t } = useTranslation();

  useEffect(() => {
    const translatedTitle = t(pageTitle as any) || pageTitle;
    const fullTitle = suffix ? `${translatedTitle} | ${suffix}` : translatedTitle;

    // パフォーマンス最適化: DOM操作をバッチ化
    // requestAnimationFrameを使用してブラウザのペイントサイクルに合わせる
    requestAnimationFrame(() => {
      document.title = fullTitle;

      // メタタグも更新
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute(
        'content',
        `${translatedTitle} - オンライン履歴書・経歴書作成ツール`
      );

      // Open Graphタグの更新
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (!ogTitle) {
        ogTitle = document.createElement('meta');
        ogTitle.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitle);
      }
      ogTitle.setAttribute('content', fullTitle);
    });

    // ページ変更をスクリーンリーダーに通知（パフォーマンス最適化：共通ユーティリティを使用）
    announce(`${translatedTitle}ページに移動しました`, 'polite', 2000);
  }, [pageTitle, suffix, t]);
};

/**
 * メタタグ管理フック
 *
 * アクセシビリティに関連するメタタグを管理します。
 *
 * @param {Object} options - オプション
 * @returns {void}
 */
export const useAccessibilityMeta = (options?: {
  viewport?: string;
  themeColor?: string;
  colorScheme?: 'light' | 'dark' | 'auto';
}): void => {
  useEffect(() => {
    // パフォーマンス最適化: DOM操作をバッチ化
    requestAnimationFrame(() => {
      // ビューポート設定（ズーム可能に）
      let viewport = document.querySelector('meta[name="viewport"]');
      if (!viewport) {
        viewport = document.createElement('meta');
        viewport.setAttribute('name', 'viewport');
        document.head.appendChild(viewport);
      }
      viewport.setAttribute(
        'content',
        options?.viewport ||
          'width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes'
      );

      // テーマカラー
      if (options?.themeColor) {
        let themeColor = document.querySelector('meta[name="theme-color"]');
        if (!themeColor) {
          themeColor = document.createElement('meta');
          themeColor.setAttribute('name', 'theme-color');
          document.head.appendChild(themeColor);
        }
        themeColor.setAttribute('content', options.themeColor);
      }

      // カラースキーム
      if (options?.colorScheme) {
        let colorScheme = document.querySelector('meta[name="color-scheme"]');
        if (!colorScheme) {
          colorScheme = document.createElement('meta');
          colorScheme.setAttribute('name', 'color-scheme');
          document.head.appendChild(colorScheme);
        }
        colorScheme.setAttribute(
          'content',
          options.colorScheme === 'auto' ? 'light dark' : options.colorScheme
        );
      }
    });

    // プリファレンスに基づくメディアクエリのサポート
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const prefersContrast = window.matchMedia('(prefers-contrast: high)');
    const prefersColorScheme = window.matchMedia('(prefers-color-scheme: dark)');

    // ボディにクラスを追加
    if (prefersReducedMotion.matches) {
      document.body.classList.add('reduced-motion');
    }
    if (prefersContrast.matches) {
      document.body.classList.add('high-contrast');
    }
    if (prefersColorScheme.matches) {
      document.body.classList.add('dark-mode');
    }

    // リスナーの登録
    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        document.body.classList.add('reduced-motion');
      } else {
        document.body.classList.remove('reduced-motion');
      }
    };

    const handleContrastChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        document.body.classList.add('high-contrast');
      } else {
        document.body.classList.remove('high-contrast');
      }
    };

    const handleColorSchemeChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
    };

    prefersReducedMotion.addEventListener('change', handleReducedMotionChange);
    prefersContrast.addEventListener('change', handleContrastChange);
    prefersColorScheme.addEventListener('change', handleColorSchemeChange);

    return () => {
      prefersReducedMotion.removeEventListener('change', handleReducedMotionChange);
      prefersContrast.removeEventListener('change', handleContrastChange);
      prefersColorScheme.removeEventListener('change', handleColorSchemeChange);
    };
  }, [options]);
};
